import { fileExtensionIsAccepted, formatBytes } from "./files.js"

const defaultFileSelectionMessages = {
  choose_one_again: "Choose the file again before continuing.",
  choose_all_again: "Choose all {{count}} files again before continuing.",
  choose_at_least_one: "Choose at least one document or folder before continuing.",
  remove_one_unsupported: "Remove the unsupported file before continuing.",
  remove_unsupported: "Remove {{count}} unsupported files before continuing.",
  remove_one_empty: "Remove the empty file before continuing.",
  remove_empty: "Remove {{count}} empty files before continuing.",
  remove_one_oversized: "Remove the file larger than {{size}} before continuing.",
  remove_oversized: "Remove {{count}} files larger than {{size}} before continuing.",
  total_must_fit: "The selected files must total {{size}} or less."
}

export function interpolateMessage(template, values = {}) {
  return Object.entries(values).reduce(
    (message, [key, value]) => message.replaceAll(`{{${key}}}`, String(value)),
    template
  )
}

function selectionMessage(messages, key, values = {}) {
  return interpolateMessage(messages[key] || defaultFileSelectionMessages[key], values)
}

export function validateUploadForm({
  form,
  summary,
  files,
  filesInput,
  folderInput,
  activeInput,
  reviewedFileCount,
  maxFileSize,
  maxTotalSize
}) {
  const input = activeInput || filesInput
  filesInput.setCustomValidity("")
  folderInput.setCustomValidity("")

  const fileMessage = fileSelectionError({
    files,
    accept: filesInput.accept,
    reviewedFileCount,
    maxFileSize,
    maxTotalSize
  })
  input.setCustomValidity(fileMessage || "")

  const invalidFields = Array.from(form.querySelectorAll(":invalid"))

  if (invalidFields.length === 0) {
    summary.hidden = true
    return { invalidFields, fileMessage }
  }

  invalidFields.forEach(field => {
    field.classList.add("error")
    field.dataset.clientInvalid = "true"
    field.setAttribute("aria-invalid", "true")
  })
  if (fileMessage) {
    const visibleInvalidFields = invalidFields.filter(field => (
      field !== filesInput && field !== folderInput
    ))
    if (visibleInvalidFields.length === 0) {
      summary.hidden = true
    } else {
      updateSummary(summary, visibleInvalidFields.length)
    }
  } else {
    updateSummary(summary, invalidFields.length)
  }

  return { invalidFields, fileMessage }
}

export function fileSelectionError({
  files,
  accept,
  reviewedFileCount,
  maxFileSize,
  maxTotalSize,
  messages = {}
}) {
  if (files.length === 0) {
    if (reviewedFileCount === 1) return selectionMessage(messages, "choose_one_again")
    if (reviewedFileCount > 1) {
      return selectionMessage(messages, "choose_all_again", { count: reviewedFileCount })
    }

    return selectionMessage(messages, "choose_at_least_one")
  }

  const unsupportedCount = files.filter(file => (
    !fileExtensionIsAccepted(file.name, accept)
  )).length
  if (unsupportedCount === 1) return selectionMessage(messages, "remove_one_unsupported")
  if (unsupportedCount > 1) {
    return selectionMessage(messages, "remove_unsupported", { count: unsupportedCount })
  }

  const emptyCount = files.filter(file => file.size === 0).length
  if (emptyCount === 1) return selectionMessage(messages, "remove_one_empty")
  if (emptyCount > 1) return selectionMessage(messages, "remove_empty", { count: emptyCount })

  const oversizedCount = files.filter(file => file.size > maxFileSize).length
  const maxFileSizeLabel = formatBytes(maxFileSize)
  if (oversizedCount === 1) {
    return selectionMessage(messages, "remove_one_oversized", { size: maxFileSizeLabel })
  }
  if (oversizedCount > 1) {
    return selectionMessage(messages, "remove_oversized", {
      count: oversizedCount,
      size: maxFileSizeLabel
    })
  }

  if (files.reduce((sum, file) => sum + file.size, 0) > maxTotalSize) {
    return selectionMessage(messages, "total_must_fit", { size: formatBytes(maxTotalSize) })
  }

  return null
}

export function clearClientError(field) {
  if (!field.dataset.clientInvalid || !field.validity.valid) return

  field.classList.remove("error")
  field.removeAttribute("data-client-invalid")
  field.setAttribute("aria-invalid", "false")
}

export function refreshValidationSummary(form, summary) {
  const invalidFields = Array.from(form.querySelectorAll("[data-client-invalid]"))
    .filter(field => !field.validity.valid)

  if (invalidFields.length === 0) {
    summary.hidden = true
    return
  }

  updateSummary(summary, invalidFields.length)
}

function updateSummary(summary, count) {
  summary.textContent = count === 1
    ? "Review the highlighted field before continuing."
    : `Review ${count} highlighted fields before continuing.`
  summary.hidden = false
}
